import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	EmptyList,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { GET_QUERY_KEY, QUERY_OPTIONS } from "../../../common/constants";
import { IResponse, IServerError } from "../../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	formatDate,
	sortDDLByLabel,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount,
} from "../../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CRACreditService } from "../../../services/CRACreditService";

import { EmployeeService } from "../../../services/EmployeeService";
import FileSaver from "file-saver";
import { IAppState } from "../../../store/reducers";
import { IDropDownListResponse } from "../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../models";
import { ReportsService } from "../../../services/ReportsService";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";
import { VacationPayReportHeader } from "./VacationPayReportHeader";
import { VacationPayDataGridWithColumnToggled } from "./VacationPayDataGridWithColumnToggled";

export interface IVacationPayReportSetup { }
const VACATION_PAY_REPORT_QUERY_KEY = "vacation-pay-report";

const useVacationPayHolidayReport = (
	runGetListAPICall: boolean,
	clientId: string,
	provinceId: string,
	holidayDate: string,
	employeeId: string,
	payPeriodEndDate: string,
	payPeriod: string,
	startDate: string | null,
	endDate: string | null,
	params: IGetListRequest,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			VACATION_PAY_REPORT_QUERY_KEY,
			runGetListAPICall,
			clientId,
			provinceId,
			holidayDate,
			employeeId,
			payPeriodEndDate,
			payPeriod,
			startDate,
			endDate,
			params,
		],
		queryFn: () =>
			ReportsService.getVacationPayReport(
				runGetListAPICall,
				clientId,
				provinceId,
				holidayDate,
				employeeId,
				payPeriodEndDate,
				payPeriod,
				startDate,
				endDate,
				params,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});


const useGetPayPeriodEndDateDDL = (
	clientId: string,
	employeePayPeriodId: string | null,
	taxYear: number
) =>
	useQuery({
		queryKey: [
			"periods-ddl",
			employeePayPeriodId,
		],
		queryFn: () => {
			return CRACreditService.getPayPeriodDDL(
				clientId,
				employeePayPeriodId,
				taxYear
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetEmployeeDDL = (clientId: string) =>
	useQuery({
		queryKey: ["Employee-ddl", clientId],
		queryFn: () => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});

const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["pay-frequency-ddl"],
		queryFn: (data) => {
			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

export const VacationPayReport: React.FC<IVacationPayReportSetup> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoading, setIsLoading] = useState(false);
	const [vacationPayReportData, setVacationPayReportData] =
		useState<any>(undefined);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);
	const [selectedPeriodEndDate, setSelectedPeriodEndDate] = useState<string | null>(null);
	

	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});


	

	const provinceId = formMethods.getValues("provinceId");
	const holidayDate = formMethods.getValues("holidayDate");
	const employeeId = formMethods.getValues("employeeId");
	const payPeriodEndDate = formMethods.getValues("payPeriodEndDate");
	const payPeriod = formMethods.getValues("payPeriod");
	const startDate = formMethods.getValues("startDate");
	const endDate = formMethods.getValues("endDate");

	const { isPending, data } = useVacationPayHolidayReport(
		runGetListAPICall,
		clientIdStore,
		provinceId,
		holidayDate,
		employeeId,
		payPeriodEndDate,
		payPeriod,
		formatDate(startDate),
		formatDate(endDate),
		queryParams,
		navigateToLogout
	);
	const vacationPayReportList: any | null = data;
	useEffect(() => {
		if (vacationPayReportList) {
			setVacationPayReportData(vacationPayReportList);
		}
	}, [vacationPayReportList]);

	const { isLoading: isLoadingemployeeDDL, data: employeeDDL } = useGetEmployeeDDL(clientId);
	const { isLoading: isLoadingPayrPeriodFrequencyDDL, data: payPeriodFrequnecyDDL } = useGetPayFrequencyDDL(clientId, taxYear);
	const { isLoading: isLoadingPayPeriodEndDateDDL, data: payPeriodEndDateDDL } = useGetPayPeriodEndDateDDL(clientId, selectedPeriodEndDate, taxYear);

	const employeeDDLItems = useMemo(() => {
		if (employeeDDL) {
			if (
				employeeDDL &&
				Array.isArray(employeeDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(employeeDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [employeeDDL]);

	const payPeriodEndDateDDLItems = useMemo(() => {
		if (payPeriodEndDateDDL) {
			if (
				payPeriodEndDateDDL &&
				Array.isArray(payPeriodEndDateDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(payPeriodEndDateDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [{
				label: "All",
				value: "all",
			}];
		}
		return [{
			label: "All",
			value: "all",
		}];
	}, [payPeriodEndDateDDL]);


	const payPeriodFrequencyDDLItems = useMemo(() => {
		if (payPeriodFrequnecyDDL) {
			if (
				payPeriodFrequnecyDDL &&
				Array.isArray(payPeriodFrequnecyDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(payPeriodFrequnecyDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [payPeriodFrequnecyDDL]);

	//   after click apply button api calls with updated values and update vacation pay report data
	const VacationPayReportDataAPICall = (
		provinceId: string,
		holidayDate: string,
		employeeId: string,
		payPeriodEndDate: string,
		payPeriod: string,
		startDate: string | null,
		endDate: string | null,
	) => {
		const fetchData = async () => {
			setIsLoading(true);

			try {
				const response: any = await ReportsService.getVacationPayReport(
					true,
					clientIdStore,
					provinceId,
					holidayDate,
					employeeId,
					payPeriodEndDate,
					payPeriod,
					formatDate(startDate),
					formatDate(endDate),
					queryParams,
					navigateToLogout
				);

				if (response) {
					setVacationPayReportData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {

		VacationPayReportDataAPICall(
			values.provinceId,
			values.holidayDate,
			values.employeeId,
			values.payPeriodEndDate,
			values.payPeriod,
			values.startDate,
			values.endDate,
		);
	};

	const [zeroEmployee, setzeroEmployee] = useState(true);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);

	const {
		isPending: isSubmittingAddRemoveHeaderColumns,
		mutate: submitAddRemoveHeaderColumns,
	} = useMutation({
		mutationFn: ReportsService.getVacationPayAddRemoveHeaderColumns,
		onSuccess: (res: any | IServerError | IResponse) => {
			if (res.status === 200) {
				setRunGetListAPICall(true)
				return queryClient.invalidateQueries({
					queryKey: [VACATION_PAY_REPORT_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});


	useEffect(() => {
		if (submitAddRemoveColumnsHandler) {
			submitAddRemoveHeaderColumns({
				clientId: clientId,
				columns: submitAddRemoveColumnsHandler,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [submitAddRemoveColumnsHandler]);


	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["vacation-pay-report"]);


	const updatedVacationPayListItems = useMemo(() => {
		return (
			vacationPayReportData &&
			vacationPayReportData.items &&
			vacationPayReportData?.items.map((item: any) => ({
				...item,
			}))
		);
	}, [vacationPayReportData]);

	// // getting latest updated values of form fields using watch method
	// const { watch } = formMethods;
	// const values = watch([
	// 	"provinceId",
	// 	"holidayDate",
	// 	"employeeId",
	// 	"payPeriodEndDate",
	// 	"payPeriod",
	// 	"startDate",
	// 	"endDate",
	// ]);

	// const savePdfAsFile = (xmlData: any, filename: any) => {
	// 	const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
	// 	FileSaver.saveAs(blob, filename);
	// };

	// const onExportButtonClick = async (selectedOption: string) => {
	// 	const [provinceId, employeeId, payPeriod, holidayDate, payPeriodEndDate, startDate, endDate] = values;

	// 	try {
	// 		setIsLoading(true);
	// 		const response = await ReportsService.exportVacationPayReport(
	// 			selectedOption,
	// 			clientIdStore,
	// 			provinceId,
	// 			holidayDate,
	// 			employeeId,
	// 			payPeriodEndDate,
	// 			payPeriod,
	// 			formatDate(startDate),
	// 			formatDate(endDate),
	// 			queryParams
	// 		);

	// 		if (response) {
	// 			if (selectedOption === "PDF") {
	// 				if (response.status === 400) {
	// 					setSnackbarData({
	// 						open: true,
	// 						message: response.data.message
	// 							? response.data.message
	// 							: "Something went wrong.",

	// 						severity: "error",
	// 					});
	// 				} else {
	// 					const filename = `${clientName}-holiday.pdf`;
	// 					savePdfAsFile(response, filename);
	// 				}
	// 			} else if (selectedOption === "XLS") {
	// 				if (response.status === 400) {
	// 					setSnackbarData({
	// 						open: true,
	// 						message: response.data.message
	// 							? response.data.message
	// 							: "Something went wrong.",

	// 						severity: "error",
	// 					});
	// 				} else {
	// 					const filename = `${clientName}-holiday.xlsx`;
	// 					savePdfAsFile(response, filename);
	// 				}
	// 			} else {
	// 				const fileType =
	// 					selectedOption === "XLS"
	// 						? "application/vnd.ms-excel"
	// 						: "text/csv;charset=utf-8;";
	// 				const fileExtension = selectedOption === "XLS" ? "xls" : "csv";

	// 				const blob = new Blob([response], { type: fileType });
	// 				FileSaver.saveAs(blob, `${clientName}-holiday.${fileExtension}`);
	// 			}
	// 		} else {
	// 			console.error("Error: No data received from the server");
	// 		}
	// 	} catch (err) {

	// 		setIsLoading(false);
	// 	} finally {
	// 		setIsLoading(false);
	// 	}

	// };

	const expandedLabels = ["Reports"];

	const enhanceData = (data: any[], ancestorChain: string[] = []): any[] => {
		return data.map((item) => {
			const currentChain = [...ancestorChain, item.label];


			const isPayrollUnderReports =
				item.label === "Payroll" && ancestorChain.includes("Reports");


			const isVacationPayUnderPayroll =
				item.label === "Vacation Pay" &&
				ancestorChain.includes("Reports") &&
				ancestorChain.includes("Payroll");

			const isExpanded =
				expandedLabels.includes(item.label) || // e.g. "Reports"
				isPayrollUnderReports ||
				isVacationPayUnderPayroll;

			return {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items
					? enhanceData(item.items, currentChain)
					: undefined,
				path: isVacationPayUnderPayroll
					? "/reports/vacation-pay"
					: item.path ?? undefined,
			};
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);

	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return (
		<>

			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Vacation Pay"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<VacationPayReportHeader
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
						employeeDDLItems={employeeDDLItems}
						payPeriodFrequencyDDLItems={payPeriodFrequencyDDLItems}
						payPeriodEndDateDDL={payPeriodEndDateDDLItems ?? []}
						onPeriodEndDateSelectionChangeHandler={(value: string) => setSelectedPeriodEndDate(value)}
						formMethods={formMethods}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending ||
						isSubmittingAddRemoveHeaderColumns ||
						isLoading || isLoadingPayrPeriodFrequencyDDL || isLoadingemployeeDDL || isLoadingPayPeriodEndDateDDL 
					}
					testId="vacation-pay-report"
				/>

				<VacationPayDataGridWithColumnToggled
					columns={
						vacationPayReportData?.headerColumns
							? vacationPayReportData.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
						// vacationPayReportData?.headerColumns ?? []
					}
					completeHeaderColumnsList={
						vacationPayReportData
							&& vacationPayReportData
								?.totalHeaderColumns &&
							Object.keys(vacationPayReportData.totalHeaderColumns).length !== 0
							? vacationPayReportData.totalHeaderColumns
							: {}
						// vacationPayReportData?.headerColumns ?? []
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						vacationPayReportData && updatedVacationPayListItems
							? updatedVacationPayListItems
							: []
						// vacationPayReportData?.items ?? []
						// itams
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={false}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						vacationPayReportData && vacationPayReportData.total
							? vacationPayReportData.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search by Name"}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"Vacation Pay"}
					// onExportButtonClick={onExportButtonClick}
				/>

				{vacationPayReportData ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}
			</Grid>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
